/**
 * @file     presalesModel.ts
 * @purpose  Client-safe constants and pure helpers for the /presales family:
 *           the inventory floors, the project slug, the price-gap reading and
 *           the completion-quarter buckets. No database imports, so page
 *           eligibility and client components can read them.
 */
import { buildingSlug } from './buildingSlug';

/** A city page needs this many presales before it is indexable. */
export const PRESALE_CITY_FLOOR = 5;
/** A project page needs this many displayable units; v_presale_projects enforces it too. */
export const PRESALE_PROJECT_FLOOR = 3;

export function isPresaleCityEligible(presaleCount: number): boolean {
  return Number.isFinite(presaleCount) && presaleCount >= PRESALE_CITY_FLOOR;
}

/** `/presales/project/[slug]`: the building slug space, so a finished phase's
 *  `/buildings/[slug]` and its presale record share one spelling. */
export function presaleProjectSlug(name: string, city: string | null): string {
  return buildingSlug(name, city);
}

/** "Beaumont by Polygon" splits into the project and its developer; most do not. */
export function splitDeveloper(name: string): { project: string; developer: string | null } {
  const m = /^(.+?)\s+by\s+(.+)$/i.exec(name.trim());
  if (!m) return { project: name.trim(), developer: null };
  return { project: m[1].trim(), developer: m[2].trim() };
}

/**
 * Quarter buckets for the completion strip: every quarter from the first
 * stated one (or the current one) through six quarters out, then one "later"
 * bucket, so a single far-off listing does not stretch the strip across years.
 */
export function completionQuarterBuckets(
  quarters: Array<{ quarter: string; n: number }>,
  now = new Date(),
): Array<{ label: string; n: number }> {
  const parsed = quarters
    .map((q) => {
      const m = /^(\d{4})-([1-4])$/.exec(q.quarter);
      return m ? { year: Number(m[1]), quarter: Number(m[2]), n: q.n } : null;
    })
    .filter((q): q is { year: number; quarter: number; n: number } => q != null);
  if (parsed.length === 0) return [];
  const index = (y: number, q: number) => y * 4 + (q - 1);
  const nowIndex = index(now.getFullYear(), Math.floor(now.getMonth() / 3) + 1);
  const first = Math.min(nowIndex, ...parsed.map((q) => index(q.year, q.quarter)));
  const last = first + 5;
  const buckets = new Map<number, number>();
  let later = 0;
  for (const q of parsed) {
    const i = index(q.year, q.quarter);
    if (i > last) later += q.n;
    else buckets.set(i, (buckets.get(i) ?? 0) + q.n);
  }
  const out: Array<{ label: string; n: number }> = [];
  for (let i = first; i <= last; i++) {
    out.push({ label: `Q${(i % 4) + 1} ${Math.floor(i / 4)}`, n: buckets.get(i) ?? 0 });
  }
  // Trim empty ends; keep interior gaps so a gap reads as a gap.
  while (out.length && out[0].n === 0) out.shift();
  while (out.length && out[out.length - 1].n === 0) out.pop();
  if (later > 0) out.push({ label: `${Math.floor((last + 1) / 4)} or later`, n: later });
  return out;
}

/** The price-gap reading the city page leads with, or null when either side is missing. */
export function priceGap(
  presaleMedian: number | null,
  resaleMedian: number | null,
): { diff: number; pct: number; direction: 'less' | 'more' | 'same' } | null {
  if (presaleMedian == null || resaleMedian == null || resaleMedian <= 0) return null;
  const diff = presaleMedian - resaleMedian;
  const pct = Math.round((Math.abs(diff) / resaleMedian) * 100);
  return { diff, pct, direction: pct === 0 ? 'same' : diff < 0 ? 'less' : 'more' };
}

export const cad = (n: number): string => `$${Math.round(n).toLocaleString('en-CA')}`;

/**
 * The completion pill reads "Est. {when}", so a phrase that already opens with
 * "Estimated completion:" or "Completion" is trimmed to the date part. The
 * project facts keep the full quoted phrase; only the card pill is compacted.
 */
export function compactCompletion(estimate: string): string {
  return estimate
    .replace(/^(?:est(?:imated)?\.?|anticipated|expected|projected|scheduled|target(?:ed)?)\s+(?:for\s+)?/i, '')
    .replace(/^(?:completion|occupancy|move[- ]in|completing|completes)(?:\s+(?:date|is|in|by|for|estimated|expected|anticipated|scheduled|set))?\s*[:\-]?\s*/i, '')
    .trim() || estimate;
}
